import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { listEPKs, deleteEPK, updateEPKStatus } from '../lib/api';
import { Plus, Archive, Trash2, ExternalLink, Eye, ArchiveRestore, Copy, Pencil } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

export default function AdminDashboard() {
  const [epks, setEpks] = useState([]);
  const [filter, setFilter] = useState('active');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [copied, setCopied] = useState(null);
  const { logout } = useAuth();
  const navigate = useNavigate();

  const loadEPKs = async () => {
    try {
      const { data } = await listEPKs(filter === 'all' ? null : filter);
      setEpks(data);
    } catch (err) {
      console.error(err);
      if (err.response?.status === 401) {
        logout();
      }
    }
  };

  useEffect(() => { loadEPKs(); }, [filter]);

  const handleArchive = async (id, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'archived' : 'active';
    await updateEPKStatus(id, newStatus);
    loadEPKs();
  };

  const handleDelete = async () => {
    if (deleteTarget) {
      await deleteEPK(deleteTarget);
      setDeleteTarget(null);
      loadEPKs();
    }
  };

  const copyLink = async (slug) => {
    const url = `${window.location.origin}/epk/${slug}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = url;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(slug);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div className="admin-layout">
      {/* Nav */}
      <nav className="admin-nav">
        <img src="/AC_logo_longbeige.png" alt="ALL CAPS" />
        <div className="flex items-center gap-4">
          <button data-testid="create-epk-btn" onClick={() => navigate('/admin/epk/new')} className="admin-btn-primary" style={{ background: 'var(--admin-bg)', color: 'var(--admin-primary)' }}>
            <span className="flex items-center gap-2"><Plus size={16} /> NEUES EPK</span>
          </button>
          <button data-testid="logout-btn" onClick={logout} className="text-sm uppercase tracking-wider" style={{ color: 'var(--admin-bg)', fontFamily: 'var(--font-heading)' }}>
            ABMELDEN
          </button>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        <h1 className="text-4xl font-black mb-8" style={{ fontFamily: 'var(--font-heading)' }}>EPK VERWALTUNG</h1>

        {/* Filter */}
        <div className="flex gap-0 mb-8 border border-[var(--admin-primary)]" style={{ display: 'inline-flex' }}>
          {['active', 'archived', 'all'].map((f) => (
            <button
              key={f}
              data-testid={`filter-${f}-btn`}
              onClick={() => setFilter(f)}
              className="px-4 py-2 text-sm uppercase tracking-wider font-bold transition-colors"
              style={{
                fontFamily: 'var(--font-heading)',
                background: filter === f ? 'var(--admin-primary)' : 'transparent',
                color: filter === f ? 'var(--admin-bg)' : 'var(--admin-primary)',
                borderRight: f !== 'all' ? '1px solid var(--admin-primary)' : 'none',
              }}
            >
              {f === 'active' ? 'AKTIV' : f === 'archived' ? 'ARCHIVIERT' : 'ALLE'}
            </button>
          ))}
        </div>

        {/* EPK List */}
        {epks.length === 0 ? (
          <div className="admin-card text-center py-12" data-testid="empty-state">
            <p className="text-lg" style={{ color: 'var(--admin-text-muted)' }}>Keine EPKs vorhanden</p>
            <button onClick={() => navigate('/admin/epk/new')} className="admin-btn-primary mt-4">
              <span className="flex items-center gap-2 justify-center"><Plus size={16} /> ERSTES EPK ERSTELLEN</span>
            </button>
          </div>
        ) : (
          <div className="space-y-0">
            {epks.map((epk) => (
              <div
                key={epk.id}
                data-testid={`epk-row-${epk.id}`}
                className="flex items-center justify-between px-6 py-4 border border-[var(--admin-primary)] -mt-px bg-[var(--admin-surface)] hover:bg-[var(--admin-bg)] transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3">
                    <h3 className="text-lg font-bold truncate" style={{ fontFamily: 'var(--font-heading)' }}>
                      {epk.basic_info?.artist_name || 'UNBENANNT'}
                    </h3>
                    {epk.status === 'archived' && (
                      <span className="text-xs px-2 py-0.5 border" style={{ color: 'var(--admin-text-muted)', borderColor: 'var(--admin-text-muted)' }}>ARCHIVIERT</span>
                    )}
                  </div>
                  <p className="text-sm mt-1" style={{ color: 'var(--admin-text-muted)' }}>
                    {epk.basic_info?.genre} &mdash; /{epk.slug}
                  </p>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <button
                    data-testid={`copy-link-${epk.id}`}
                    onClick={() => copyLink(epk.slug)}
                    className="p-2 hover:bg-[var(--admin-primary)] hover:text-[var(--admin-bg)] transition-colors"
                    title="Link kopieren"
                  >
                    <Copy size={16} />
                    {copied === epk.slug && <span className="absolute text-xs bg-black text-white px-2 py-1 -mt-8 ml-4">Kopiert!</span>}
                  </button>
                  <button
                    data-testid={`view-epk-${epk.id}`}
                    onClick={() => window.open(`/epk/${epk.slug}`, '_blank')}
                    className="p-2 hover:bg-[var(--admin-primary)] hover:text-[var(--admin-bg)] transition-colors"
                    title="EPK ansehen"
                  >
                    <Eye size={16} />
                  </button>
                  <button
                    data-testid={`edit-epk-${epk.id}`}
                    onClick={() => navigate(`/admin/epk/${epk.id}`)}
                    className="p-2 hover:bg-[var(--admin-primary)] hover:text-[var(--admin-bg)] transition-colors"
                    title="Bearbeiten"
                  >
                    <Pencil size={16} />
                  </button>
                  <button
                    data-testid={`archive-epk-${epk.id}`}
                    onClick={() => handleArchive(epk.id, epk.status)}
                    className="p-2 hover:bg-[var(--admin-primary)] hover:text-[var(--admin-bg)] transition-colors"
                    title={epk.status === 'active' ? 'Archivieren' : 'Aktivieren'}
                  >
                    {epk.status === 'active' ? <Archive size={16} /> : <ArchiveRestore size={16} />}
                  </button>
                  <button
                    data-testid={`delete-epk-${epk.id}`}
                    onClick={() => setDeleteTarget(epk.id)}
                    className="p-2 hover:bg-[var(--admin-danger)] hover:text-white transition-colors"
                    title="Löschen"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>EPK LÖSCHEN?</AlertDialogTitle>
            <AlertDialogDescription>
              Dieses EPK wird unwiderruflich gelöscht. Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>ABBRECHEN</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-btn" onClick={handleDelete} className="bg-[var(--admin-danger)] text-white">
              LÖSCHEN
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
